import { Button, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { StepItem, Steps } from 'components/molecules/Steps'
import { useStep } from 'components/molecules/Steps/hook'
import { useInstitutionsContext } from 'contexts/institutions'
import { ErrorResponseDTO, InstitutionsRequestDTO } from 'models'
import { errosInstitution } from 'pages/Institution/utils/errosInstitution'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Address from '../Address'
import Data from '../Data'
import Responsible from '../Responsible'
import { StepsFormProps, StepsProps } from './interface'
import { Schema } from './schema'
import { Form, FormActions } from './styles'

const StepsForm: React.FC<StepsProps> = ({ onSubmit, onError, onCancel }) => {
    const [currentStep, handles, untilStep] = useStep(3, true)
    const { goToNextStep, goToPrevStep, setStep } = handles
    const { createInstitutions } = useInstitutionsContext()
    const toast = useToast()

    const [stepperValid, setStepperValid] = useState({
        data: false,
        address: false,
        responsible: false
    })

    const methods = useForm<StepsFormProps>({
        resolver: yupResolver(Schema),
        mode: 'onChange'
    })

    const { handleSubmit, trigger, watch, getFieldState, formState } = methods

    const handleNext = async (
        e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        let valid = false

        switch (currentStep) {
            case 1:
                valid = await trigger('data')
                e?.preventDefault()
                break
            case 2:
                valid = await trigger('address')
                e?.preventDefault()
                break
            case 3:
                // valid = await trigger('responsible')
                break
        }

        if (valid) {
            goToNextStep()
        }
    }

    const handlePrev = () => {
        currentStep === 1 ? onCancel && onCancel() : goToPrevStep()
    }

    const onFormSubmit = (institution: StepsFormProps) => {
        const { data, address, responsible } = institution
        const params: InstitutionsRequestDTO = {
            razaoSocial: data.razaoSocial,
            cnpj: data.cnpj.replace(/\D/g, ''),
            ie: data.IE,
            im: data.IM,
            email: data.email,
            telefone: data.contato.replace(/\D/g, ''),
            enderecos: address.form,
            responsaveisLegais: responsible.form.map(res => ({
                nome: res.nome,
                cpf: res.cpf.replace(/\D/g, ''),
                telefone: res.contato.replace(/\D/g, ''),
                email: res.email,
                funcao: res.funcao
            }))
        }

        return createInstitutions(params)
            .then(_ => {
                onSubmit && onSubmit()
            })
            .catch(({ response }) => {
                const { data } = response as ErrorResponseDTO
                data.filter(
                    (elem, index, arr) =>
                        index ===
                        arr.findIndex(
                            checkEle => checkEle.codigo === elem.codigo
                        )
                ).map(res => {
                    const error = errosInstitution[res.codigo] || {}
                    toast({
                        title: error.title || 'Falha no Cadastro',
                        description: error.description || res.mensagem,
                        position: 'bottom-left',
                        status: 'error',
                        isClosable: true,
                        variant: 'solid-error'
                    })
                })
            })
    }

    useEffect(() => {
        const subscription = watch(async () => {
            setStepperValid({
                data: await Promise.resolve(!getFieldState('data').error),
                address: await Promise.resolve(!getFieldState('address').error),
                responsible: await Promise.resolve(
                    !getFieldState('responsible').error
                )
            })
        })
        return () => subscription.unsubscribe()
    }, [watch])

    const steps = [
        {
            label: 'Instituição financeira',
            content: <Data />,
            valid: stepperValid.data,
            dataTestid: 'InstituiçãoFinanceira',
            onClick: () => setStep(1)
        },
        {
            label: 'Endereços',
            content: <Address />,
            valid: stepperValid.address,
            dataTestid: 'EndereçosIf',
            onClick: () => setStep(2)
        },
        {
            label: 'Responsáveis legais',
            content: <Responsible />,
            valid: stepperValid.responsible,
            dataTestid: 'responsibleIf',
            onClick: () => setStep(3)
        }
    ]

    return (
        <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(onFormSubmit)}>
                <Steps
                    currentStep={currentStep}
                    untilStep={untilStep}
                    borderTopRadius={6}
                    background="primary.700"
                    color="white"
                >
                    {steps.map(({ label, content, valid, onClick }) => (
                        <StepItem
                            key={label}
                            aria-label={label}
                            valid={valid}
                            onClick={onClick}
                        >
                            {content}
                        </StepItem>
                    ))}
                </Steps>
                <FormActions>
                    <Button
                        data-testid="form-action-cancel"
                        colorScheme="primary"
                        variant="outline"
                        onClick={handlePrev}
                        disabled={formState.isSubmitting}
                    >
                        {currentStep === 1 ? 'Cancelar' : 'Voltar'}
                    </Button>
                    <Button
                        data-testid={`form-action-confirm-${currentStep}`}
                        colorScheme="primary"
                        type={
                            currentStep === steps.length ? 'submit' : 'button'
                        }
                        onClick={e => handleNext(e)}
                        isLoading={formState.isSubmitting}
                        isDisabled={formState.isSubmitting}
                        // disabled={
                        //     !Object.values(stepperValid).at(
                        //         currentStep - 1 ?? 0
                        //     )
                        // }
                    >
                        {currentStep === steps.length
                            ? 'Concluir cadastro'
                            : 'Continuar'}
                    </Button>
                </FormActions>
            </Form>
        </FormProvider>
    )
}

export default StepsForm
